import _ from "lodash";
import { cloneEntityWithoutSpecificFields, rearangeDateRanges } from "../../../components/shared/utils";
import moment from "moment";
import { RatePricingTypesEnum } from "../../../components/shared/constants";

const defaultRPEgetRange = rpe => {
  return { range: [moment(rpe.StartDate).startOf("day"), moment(rpe.EndDate).startOf("day")], weekdays: rpe.Weekdays };
}
const defaultRPEsetRange = (rpe, range) => {
    rpe.StartDate = range[0]; rpe.EndDate = range[1]; rpe.Weekdays = range.weekdays || rpe.Weekdays;
};
const defaultRPEdeleteElement = (rpe) => { rpe.IsActive = false; rpe.DoNotDistribute = true; }
export const defaultRPEcopyElement = (x, startDate, endDate) => ({
    ...x,
    Id: 0,
    StartDate: startDate,
    EndDate: endDate,
    RatePerPeople: x.RatePerPeople?.map(r => ({
        RateId: r.RateId,
        RoomTypeId: r.RoomTypeId,
        RatePerPersonPrices: r.RatePerPersonPrices?.map(rpp => ({
            ...rpp,
            Id: 0,
            RatePerPersonId: 0
        }))
    })),
    RatePerRooms: x.RatePerRooms?.map(r => ({
        AmountAfterTax: r.AmountAfterTax,
        AmountBeforeTax: r.AmountBeforeTax,
        RateId: r.RateId,
        RoomTypeId: r.RoomTypeId
    })),
    HotelAddons: x.HotelAddons?.map(ha => ({
        ...ha,
        Id: 0,
        StartDate: startDate,
        EndDate: endDate,
        RatePricingElementId: null,
        HotelAddonsPrices: ha.HotelAddonsPrices?.map(hap => ({
            ...hap,
            Id: 0,
            HotelAddonId: null,
        }))
    })),
    RatePricingElementsPackages: x.RatePricingElementsPackages?.map(rpep => ({
        ...rpep,
        Id: 0,
        RatePricingElementId: 0
    })),
    RPEs: x.RPEs?.map(rpe => defaultRPEcopyElement(rpe, startDate, endDate))
});


export const RPEsEditRearangeDateranges = (edited, pricings, baseRateId) => {
    const pricesWithoutEditedPrice = rpe => {
        if (!rpe.StartDate || !rpe.EndDate) return false;
        if (rpe === edited || (edited.GroupBeforeEditing && rpe === edited.GroupBeforeEditing)) return false;
        if (edited.Id && rpe.Id && edited.Id === rpe.Id) return false;
        if (edited.TempId && rpe.TempId && edited.TempId === rpe.TempId) return false;
        return true;
    }
    const filtered = pricings.filter(pricesWithoutEditedPrice);
    return RPEsRearangeDateRanges(edited, filtered, baseRateId)
}

export const RPEsRearangeDateRanges = (anchor, elements, baseRateId) => {
    const options = {
        getRange: defaultRPEgetRange,
        setRange: defaultRPEsetRange,
        deleteElement: defaultRPEdeleteElement,
        predicate: (rpe) => (rpe.IsActive && rpe.RateId !== baseRateId && !rpe.IsDummy && rpe.StartDate && rpe.EndDate),
        copyElement: defaultRPEcopyElement
    }
    return rearangeDateRanges(anchor, elements, options)
}

export const setRPEsWithRearangedExceptions = (rpes, exceptionsListPropertyName) => {
    let exceptions = _.flatten(rpes.map(r => r[exceptionsListPropertyName].filter(rpe => rpe.ExceptionDate && rpe.Id)));
    rpes = rpes.map(rpe => {
        let exceptionNoLongerInRange = rpe.IsActive ?
            rpe[exceptionsListPropertyName].filter(rpp => rpp.Id && rpp.ExceptionDate && !moment(rpp.ExceptionDate).isBetween(moment(rpe.StartDate), moment(rpe.EndDate))) :
            rpe[exceptionsListPropertyName].filter(rpe => rpe.ExceptionDate);
        let exceptionsOfRange = rpe.IsActive ? exceptions
            .filter(r => moment(r.ExceptionDate).isBetween(moment(rpe.StartDate), moment(rpe.EndDate)))
            .map(rpp => rpe.Id > 0 && rpp.RatePricingElementId === rpe.Id ? rpp : cloneEntityWithoutSpecificFields(rpp, ["Id", "RatePerPersonPrices", "RatePricingElementId"])) : [];

        return { ...rpe, [exceptionsListPropertyName]: rpe[exceptionsListPropertyName].filter(rpp => !rpp.ExceptionDate).concat(exceptionsOfRange).concat(exceptionNoLongerInRange.map(rpp => ({ ...rpp, MarkedForDelete: true }))) };
    });
}

export const areAllStartEndDatesValid = (arr) => {
    const isDateField = (field) => moment(field).isValid() && field !== undefined;
    return arr.every(obj =>
        isDateField(obj?.StartDate) &&
        isDateField(obj?.EndDate)
    );
}


export const hasZeroPrice = (rpes, requiredDesignations, RatePricingType, priceToUpdate) => {
    let activeRPEs = rpes.filter(x => x.IsActive);
    let price;
    for (let pricingElement of activeRPEs) {
      for (let requiredDesignation of requiredDesignations) {
        switch(RatePricingType){
            case RatePricingTypesEnum.PerRoom:
                price = pricingElement?.RatePerRooms?.[0];
                break;
            case RatePricingTypesEnum.PerPerson:
                price = pricingElement?.RatePerPeople?.[0]?.RatePerPersonPrices?.find(price => price.DesignationId === requiredDesignation);
                break;
            case RatePricingTypesEnum.FixPerPax:
                price = pricingElement?.RatePerPeople?.[0]?.RatePerPersonPrices?.find(price => price.DesignationId === requiredDesignation);
                break;
            default:
                return false;
            }
            if ((price?.[priceToUpdate] ?? 0) == 0) {
                return true;
            }
        }        
    }    
    return false;
}

//WIP
export const removeExceptionsOutOfRange = (rpes, getExceptions) => {
    rpes.forEach(rpe => {
        const exceptions = getExceptions(rpe)
        exceptions.forEach(exception => {
            if (exception.ExceptionDate) {
                if (!moment(exception.ExceptionDate).within(moment.range(rpe.StartDate, rpe.EndDate))) {
                    exception.MarkedForDelete = true;
                }
            }
        });
    });
}

/**
 * Inserts a copied pricing element into the pricing elements array, 
 * directly after the original element.
 *
 * @param {Array<Object>} pricingElements - The array of pricing elements.
 * @param {Object} origin - The pricing element to match (i.e., the one after which the copied element will be inserted).
 * @param {Object} copy - The copied pricing element to be inserted.
 * @returns {Array<Object>} - A new array of pricing elements with the copy inserted after the origin element.
 **/
export const insertCopiedPricing = (pricingElements, origin, copy) => {
    return pricingElements.reduce((ps, p) => {
        ps.push(p);
        if (p.Id === origin.Id) {
            ps.push(copy);
        }
        return ps;
    }, [])
}


export const getDateRangeString = (rpe, BaseId) => {
    return `${rpe.StartDate}$${rpe.EndDate}$${rpe.Weekdays}$${rpe.RateId === BaseId ? "Original" : "Derived"}$${rpe.IsActive ? "Active" : "Inactive"}`;
}